/**
 * UserSearch Component
 * 
 * Search input with recent searches stored in localStorage.
 * Displays recent searches as clickable chips.
 */

import React, { useState, useEffect, useCallback } from 'react';
import './UserSearch.scss';

interface UserSearchProps {
  onSearch: (userId: number) => void;
  initialValue?: number;
}

const STORAGE_KEY = 'rewards-admin-recent-user-searches';
const MAX_RECENT_SEARCHES = 10;

export const UserSearch: React.FC<UserSearchProps> = ({
  onSearch,
  initialValue,
}) => {
  const [searchValue, setSearchValue] = useState<string>(initialValue?.toString() || '');
  const [recentSearches, setRecentSearches] = useState<number[]>([]);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as number[];
        setRecentSearches(parsed);
      }
    } catch (err) {
      // Ignore localStorage errors
    }
  }, []);

  // Update search value when initialValue changes
  useEffect(() => {
    if (initialValue) {
      setSearchValue(initialValue.toString());
    }
  }, [initialValue]);

  // Save to recent searches
  const saveToRecentSearches = useCallback((userId: number) => {
    try {
      const updated = [
        userId,
        ...recentSearches.filter(id => id !== userId),
      ].slice(0, MAX_RECENT_SEARCHES);
      
      setRecentSearches(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (err) {
      // Ignore localStorage errors
    }
  }, [recentSearches]);

  // Handle search
  const handleSearch = useCallback(() => {
    const userId = parseInt(searchValue.trim(), 10);
    if (!isNaN(userId) && userId > 0) {
      saveToRecentSearches(userId);
      onSearch(userId);
    }
  }, [searchValue, onSearch, saveToRecentSearches]);

  // Handle Enter key
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // Handle recent search click
  const handleRecentSearchClick = useCallback((userId: number) => {
    setSearchValue(userId.toString());
    saveToRecentSearches(userId);
    onSearch(userId);
  }, [onSearch, saveToRecentSearches]);

  return (
    <div className="user-search">
      <div className="user-search-input-group">
        <input
          type="number"
          className="user-search-input"
          placeholder="Enter User ID"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyPress={handleKeyPress}
          min="1"
        />
        <button
          className="user-search-button"
          onClick={handleSearch}
          disabled={!searchValue.trim()}
        >
          Search
        </button>
      </div>
      
      {recentSearches.length > 0 && (
        <div className="user-search-recent">
          <span className="user-search-recent-label">Recent:</span>
          <div className="user-search-recent-chips">
            {recentSearches.map((userId) => (
              <button
                key={userId}
                className="user-search-recent-chip"
                onClick={() => handleRecentSearchClick(userId)}
              >
                {userId}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSearch;




